import { useMemo, useState } from 'react';
import {
  Hotel as HotelIcon,
  Lock,
  LockOpen,
  ShieldAlert,
  DoorClosed,
  Unlock,
  Thermometer,
  Lightbulb,
  Blinds,
  BellOff,
  Radio,
} from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { useConfirm } from '../components/ConfirmDialog.tsx';
import { useToast } from '../components/Toast.tsx';
import { Stagger, StaggerItem } from '../components/motion.tsx';
import { CountUp } from '../components/CountUp.tsx';
import './Hotel.css';

/* ──────────────────────────────────────────────────────────────────────────
   Door → Room mapping (the "Porte 1 → Door 1" business logic).

   In the local CDVI ATRIUM software, doors are labelled "Porte 1", "Porte 2"
   (vendorDoorId "0", "1"). The front desk thinks in rooms, so we surface each
   physical door as a guest room. This is the one place where demonstration
   metadata (room number, type, guest, housekeeping status) is layered on top
   of the REAL door — until the reservation backend (Hotel Mode build #6) lands.

   ▼ This mapping is yours to shape. Tweak room numbers, types, and the
     demo roster to match the property you're showing.
   ────────────────────────────────────────────────────────────────────────── */

export interface DoorRow {
  id: string;
  name: string;
  vendorDoorId: string;
  state: string;
  isLockdown: boolean;
  unlockTimeSeconds: number;
}

type RoomStatus = 'occupied' | 'vacant_clean' | 'vacant_dirty' | 'ooo';

export interface Room {
  door: DoorRow;
  index: number; // 1-based
  number: string;
  doorLabel: string; // "Door 1"
  porteLabel: string; // "Porte 1"
  type: string;
  status: RoomStatus;
  guest: { name: string; until: string } | null;
}

const ROOM_TYPES = ['King Suite', 'Double Queen', 'Junior Suite', 'Penthouse', 'Standard King'];
const DEMO_ROSTER: Array<{ name: string; until: string; status: RoomStatus } | null> = [
  { name: 'J. Castellano', until: 'out 11:00', status: 'occupied' },
  null, // vacant
  { name: 'M. Okafor', until: 'out tomorrow', status: 'occupied' },
  { name: 'A. Lindqvist', until: 'out 11:00', status: 'occupied' },
];

export function mapDoorsToRooms(doors: DoorRow[]): Room[] {
  return [...doors]
    .sort((a, b) => Number(a.vendorDoorId) - Number(b.vendorDoorId))
    .map((door, i) => {
      const roster = DEMO_ROSTER[i % DEMO_ROSTER.length];
      const status: RoomStatus = roster ? roster.status : i % 3 === 1 ? 'vacant_dirty' : 'vacant_clean';
      return {
        door,
        index: i + 1,
        number: String(101 + i),
        doorLabel: `Door ${i + 1}`,
        porteLabel: door.name || `Porte ${i + 1}`,
        type: ROOM_TYPES[i % ROOM_TYPES.length]!,
        status,
        guest: roster ? { name: roster.name, until: roster.until } : null,
      };
    });
}

type LockCategory = 'secured' | 'unlocked' | 'lockdown';
function lockCategory(d: DoorRow): LockCategory {
  if (d.isLockdown || d.state === 'lockdown') return 'lockdown';
  if (d.state === 'unlocked_temp' || d.state === 'open' || d.state === 'held_open' || d.state === 'forced')
    return 'unlocked';
  return 'secured';
}

const STATUS_LABEL: Record<RoomStatus, string> = {
  occupied: 'occupied',
  vacant_clean: 'vacant · clean',
  vacant_dirty: 'vacant · dirty',
  ooo: 'out of order',
};

export function Hotel() {
  const doorsQuery = trpc.door.list.useQuery(undefined, { refetchInterval: 4000 });
  const [selected, setSelected] = useState<string | null>(null);

  const rooms = useMemo(
    () => mapDoorsToRooms((doorsQuery.data as DoorRow[] | undefined) ?? []),
    [doorsQuery.data],
  );

  const occupied = rooms.filter((r) => r.status === 'occupied').length;
  const selectedRoom = rooms.find((r) => r.door.id === selected) ?? null;

  if (doorsQuery.isLoading) {
    return (
      <div className="rooms-grid">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card" style={{ height: 190 }}>
            <div className="skel" style={{ height: 28, width: 90, marginBottom: 12 }} />
            <div className="skel" style={{ height: 14, width: 160, marginBottom: 8 }} />
            <div className="skel" style={{ height: 36, width: '100%' }} />
          </div>
        ))}
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <EmptyState
        icon={HotelIcon}
        title="No rooms yet"
        body="Register a controller and bring the relay online — its doors (Porte 1, Porte 2 …) map to guest rooms here automatically."
      />
    );
  }

  return (
    <div className="stack">
      <div className="hotel-head">
        <div className="title">
          <h1>
            Front <span className="serif">desk</span>
          </h1>
          <span className="sub">14 Hudson · live rooms</span>
        </div>
        <span className="spacer" />
        <div className="occupancy">
          <div className="stat">
            <span className="n gold">
              <CountUp value={occupied} />
            </span>
            <span className="k">occupied</span>
          </div>
          <div className="stat">
            <span className="n">
              <CountUp value={rooms.length} />
            </span>
            <span className="k">rooms</span>
          </div>
        </div>
      </div>

      {/* Live floor-plan strip */}
      <div className="floorplan">
        <span className="fp-label">floor plan · live</span>
        {rooms.map((r) => {
          const cat = lockCategory(r.door);
          return (
            <button
              key={r.door.id}
              className={`fp-tile ${cat}`}
              onClick={() => setSelected(r.door.id)}
              style={{ background: 'var(--bg-2)' }}
            >
              <span className="fp-no">{r.number}</span>
              <span className="fp-state">
                <span className="fp-dot" />
                {cat}
              </span>
            </button>
          );
        })}
      </div>

      <Stagger className="rooms-grid" gap={0.06}>
        {rooms.map((r) => (
          <StaggerItem key={r.door.id}>
            <RoomCard room={r} onOpen={() => setSelected(r.door.id)} />
          </StaggerItem>
        ))}
      </Stagger>

      <RoomDetailModal
        room={selectedRoom}
        open={!!selectedRoom}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function LockBadge({ door }: { door: DoorRow }) {
  const cat = lockCategory(door);
  const Icon = cat === 'lockdown' ? ShieldAlert : cat === 'unlocked' ? LockOpen : Lock;
  return (
    <span className={`lockstate ${cat}`}>
      <Icon />
      {cat}
    </span>
  );
}

function RoomCard({ room, onOpen }: { room: Room; onOpen: () => void }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const confirm = useConfirm();
  const cat = lockCategory(room.door);

  const unlock = trpc.door.unlock.useMutation({
    onSuccess: () => {
      toast.success(`${room.number} unlocked`, `${room.porteLabel} · ${room.door.unlockTimeSeconds}s`);
      void utils.door.list.invalidate();
    },
    onError: (e) => toast.error('Unlock failed', e.message),
  });
  const lockdown = trpc.door.lockdown.useMutation({
    onSuccess: () => {
      toast.warn(`Lockdown engaged · ${room.number}`);
      void utils.door.list.invalidate();
    },
    onError: (e) => toast.error('Lockdown failed', e.message),
  });
  const release = trpc.door.releaseLockdown.useMutation({
    onSuccess: () => {
      toast.success(`Lockdown released · ${room.number}`);
      void utils.door.list.invalidate();
    },
    onError: (e) => toast.error('Release failed', e.message),
  });

  async function onLockdown() {
    const ok = await confirm({
      title: `Lock down ${room.number}?`,
      body: (
        <>
          This engages a hard lockdown on <strong>{room.porteLabel}</strong> ({room.doorLabel}). The
          guest will not be able to enter until you release it.
        </>
      ),
      confirmLabel: 'Engage lockdown',
      danger: true,
    });
    if (ok) lockdown.mutate({ doorId: room.door.id });
  }

  return (
    <div className={`room-card ${room.door.isLockdown ? 'is-lockdown' : ''}`}>
      <div className="rc-top">
        <div className="rc-no">
          <span className="num">{room.number}</span>
          <span className="type">{room.type}</span>
        </div>
        <span className="spacer" />
        <span className={`status-chip ${room.status}`}>
          <span className="d" />
          {STATUS_LABEL[room.status]}
        </span>
      </div>

      <span className="lineage" title="CDVI door label → room door">
        <span className="porte">{room.porteLabel}</span>
        <span className="arrow">→</span>
        <span>{room.doorLabel}</span>
      </span>

      {room.guest ? (
        <div className="rc-guest">
          <span className="avatar">
            {room.guest.name
              .split(/[\s.]+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((p) => p[0])
              .join('')}
          </span>
          <span className="who">
            <span className="name">{room.guest.name}</span>
            <span className="meta">{room.guest.until}</span>
          </span>
        </div>
      ) : (
        <div className="rc-vacant">no current guest</div>
      )}

      <div className="rc-actions">
        <LockBadge door={room.door} />
        <span className="spacer" />
        <button className="ghost" onClick={onOpen} title="Room detail">
          <DoorClosed size={14} /> Detail
        </button>
        {cat === 'lockdown' ? (
          <button
            className="secondary"
            disabled={release.isPending}
            onClick={() => release.mutate({ doorId: room.door.id })}
          >
            <Unlock size={14} /> Release
          </button>
        ) : (
          <>
            <button className="secondary" onClick={() => void onLockdown()} disabled={lockdown.isPending} title="Lockdown">
              <ShieldAlert size={14} />
            </button>
            <button
              disabled={unlock.isPending}
              onClick={() => unlock.mutate({ doorId: room.door.id, durationSeconds: room.door.unlockTimeSeconds })}
            >
              <Unlock size={14} /> {unlock.isPending ? 'Unlocking…' : 'Unlock'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function RoomDetailModal({ room, open, onClose }: { room: Room | null; open: boolean; onClose: () => void }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const confirm = useConfirm();
  const [duration, setDuration] = useState(5);

  const invalidate = () => void utils.door.list.invalidate();
  const unlock = trpc.door.unlock.useMutation({
    onSuccess: () => { toast.success('Unlock sent'); invalidate(); },
    onError: (e) => toast.error('Unlock failed', e.message),
  });
  const lockdown = trpc.door.lockdown.useMutation({
    onSuccess: () => { toast.warn('Lockdown engaged'); invalidate(); },
    onError: (e) => toast.error('Lockdown failed', e.message),
  });
  const release = trpc.door.releaseLockdown.useMutation({
    onSuccess: () => { toast.success('Lockdown released'); invalidate(); },
    onError: (e) => toast.error('Release failed', e.message),
  });
  const normal = trpc.door.normal.useMutation({
    onSuccess: () => { toast.success('Returned to normal'); invalidate(); },
    onError: (e) => toast.error('Normal failed', e.message),
  });

  if (!room) return null;
  const cat = lockCategory(room.door);

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth={620}
      title={
        <span className="row" style={{ gap: 10 }}>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>{room.number}</span>
          <span className="dim mono" style={{ fontSize: 11 }}>{room.type}</span>
        </span>
      }
    >
      <div className="room-detail">
        <div className="row" style={{ gap: 8 }}>
          <span className="lineage">
            <span className="porte">{room.porteLabel}</span>
            <span className="arrow">→</span>
            <span>{room.doorLabel}</span>
          </span>
          <LockBadge door={room.door} />
          <span className="spacer" />
          {room.guest && <span className="mono dim">{room.guest.name} · {room.guest.until}</span>}
        </div>

        <div>
          <div className="rd-section-label">door control</div>
          <div className="rd-controls">
            {cat === 'lockdown' ? (
              <button className="secondary" disabled={release.isPending} onClick={() => release.mutate({ doorId: room.door.id })}>
                <Unlock size={14} /> Release lockdown
              </button>
            ) : (
              <>
                <div className="rd-duration">
                  <input
                    type="number"
                    min={1}
                    max={300}
                    value={duration}
                    onChange={(e) => setDuration(Math.max(1, Math.min(300, Number(e.target.value) || 1)))}
                  />
                  <span className="u">sec</span>
                </div>
                <button
                  disabled={unlock.isPending}
                  onClick={() => unlock.mutate({ doorId: room.door.id, durationSeconds: duration })}
                >
                  <Unlock size={14} /> Unlock
                </button>
                <button
                  className="secondary"
                  disabled={lockdown.isPending}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Lock down ${room.number}?`,
                      body: <>Hard lockdown on {room.porteLabel}.</>,
                      confirmLabel: 'Engage lockdown',
                      danger: true,
                    });
                    if (ok) lockdown.mutate({ doorId: room.door.id });
                  }}
                >
                  <ShieldAlert size={14} /> Lockdown
                </button>
                <button className="ghost" disabled={normal.isPending} onClick={() => normal.mutate({ doorId: room.door.id })}>
                  Normal
                </button>
              </>
            )}
          </div>
        </div>

        <div>
          <div className="rd-section-label">in-room · matter</div>
          <div className="iot-grid">
            <div className="iot-tile">
              <span className="iot-top"><Thermometer size={14} /><span className="iot-k">climate</span></span>
              <span className="iot-v">21.0°C</span>
              <span className="iot-soon">soon</span>
            </div>
            <div className="iot-tile">
              <span className="iot-top"><Lightbulb size={14} /><span className="iot-k">lights</span></span>
              <span className="iot-v">Arrival</span>
              <span className="iot-soon">soon</span>
            </div>
            <div className="iot-tile">
              <span className="iot-top"><Blinds size={14} /><span className="iot-k">blinds</span></span>
              <span className="iot-v">Open</span>
              <span className="iot-soon">soon</span>
            </div>
            <div className="iot-tile">
              <span className="iot-top"><BellOff size={14} /><span className="iot-k">do not disturb</span></span>
              <span className="iot-v">Off</span>
              <span className="iot-soon">soon</span>
            </div>
          </div>
        </div>

        <div>
          <div className="rd-section-label">live</div>
          <div className="row mono dim" style={{ gap: 10, fontSize: 12 }}>
            <span className="live-dot" />
            {room.porteLabel} · state <span className="gold">{room.door.state}</span> · unlock {room.door.unlockTimeSeconds}s
            <Radio size={13} style={{ marginLeft: 'auto', color: 'var(--gold)' }} />
          </div>
        </div>
      </div>
    </Modal>
  );
}
